import { useState, useCallback, useEffect, useRef } from 'react'
import * as Sentry from '@sentry/react'
import { Modal, Button, Space, App } from 'antd'
import { Copy } from 'lucide-react'
import { TokenManager } from '../../../services/auth/tokenManager'
import { HttpClient } from '../../../services/api/client'
import { SparkleLoader } from '../../common/SparkleLoader'
import type { AIRequest, AIResponse, MindMapNode, MindMapEdge } from '@bandos/shared'
import {
  LoadingContainer,
  LoadingIconWrapper,
  LoadingMessage,
  ModalContentWrapper,
  ModalSecondaryText,
  PromptTextArea,
} from './styles/IdeaActionsStyles'

const LOADING_MESSAGES = [
  'Analyzing idea context...',
  'Understanding user needs...',
  'Mapping out key screens...',
  'Defining layout and interactions...',
  'Writing UI specifications...',
  'Polishing the prompt...',
]

interface FigmaExportModalProps {
  isOpen: boolean
  onClose: () => void
  mindmapId: string
  threadNodes: MindMapNode[]
  threadEdges: MindMapEdge[]
}

export function FigmaExportModal({
  isOpen,
  onClose,
  mindmapId,
  threadNodes,
  threadEdges,
}: FigmaExportModalProps) {
  const { notification: antdNotification } = App.useApp()

  const [isGenerating, setIsGenerating] = useState(false)
  const [prototypeText, setPrototypeText] = useState('')
  const [loadingMessage, setLoadingMessage] = useState(LOADING_MESSAGES[0])
  const isCancelledRef = useRef(false)

  // Generate the prompt when modal opens with valid data
  useEffect(() => {
    if (!isOpen || threadNodes.length === 0) return undefined

    isCancelledRef.current = false
    let messageInterval: ReturnType<typeof setInterval>

    const generatePrompt = async () => {
      setIsGenerating(true)
      setPrototypeText('')

      // Start rotating loading messages
      let messageIndex = 0
      messageInterval = setInterval(() => {
        messageIndex = (messageIndex + 1) % LOADING_MESSAGES.length
        setLoadingMessage(LOADING_MESSAGES[messageIndex])
      }, 2500)

      try {
        const headers = await TokenManager.getAuthHeaders()
        const requestData: AIRequest = {
          mindmapId,
          operation: 'generate_figma_prototype',
          filteredNodes: threadNodes,
          filteredEdges: threadEdges,
        }

        const response = await HttpClient.post<AIResponse>('/ai/process', requestData, headers)

        if (isCancelledRef.current) return

        if (response.error) {
          throw new Error(response.error.message || 'Failed to generate UI prompt')
        }

        if (response.data?.prototypeText) {
          setPrototypeText(response.data.prototypeText)
        } else {
          throw new Error('No prototype text returned from server')
        }
      } catch (error) {
        if (!isCancelledRef.current) {
          Sentry.captureException(error, {
            tags: { feature: 'mindmap.ideaactions', action: 'generate_figma_prototype' },
          })
          onClose()
          antdNotification.error({
            message: 'Failed to generate UI prompt',
            description: error instanceof Error ? error.message : 'An unexpected error occurred',
            title: undefined,
          })
        }
      } finally {
        if (!isCancelledRef.current) {
          clearInterval(messageInterval)
          setIsGenerating(false)
        }
      }
    }

    void generatePrompt()

    return () => {
      isCancelledRef.current = true
      clearInterval(messageInterval)
    }
  }, [isOpen, mindmapId, threadNodes, threadEdges, onClose, antdNotification])

  const handleCopyToClipboard = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(prototypeText)
      antdNotification.success({
        message: 'Copied to clipboard',
        description: 'The UI prompt has been copied to your clipboard.',
        title: undefined,
      })
    } catch (error) {
      Sentry.captureException(error, {
        tags: { feature: 'mindmap.ideaactions', action: 'copy_to_clipboard' },
      })
      antdNotification.error({
        message: 'Failed to copy',
        description: 'Could not copy text to clipboard. Please copy manually.',
        title: undefined,
      })
    }
  }, [prototypeText, antdNotification])

  const handleClose = useCallback(() => {
    if (isGenerating) {
      isCancelledRef.current = true
      setIsGenerating(false)
    }
    onClose()
  }, [isGenerating, onClose])

  return (
    <Modal
      title={
        <Space>
          <span>Export UI Prompt</span>
        </Space>
      }
      open={isOpen}
      onCancel={handleClose}
      closable
      maskClosable
      width={800}
      footer={
        isGenerating
          ? [
              <Button key="cancel" onClick={handleClose}>
                Cancel
              </Button>,
            ]
          : [
              <Button key="close" onClick={handleClose}>
                Dismiss
              </Button>,
              <Button
                type="primary"
                key="copy"
                icon={<Copy size={16} />}
                onClick={handleCopyToClipboard}
              >
                Copy to Clipboard
              </Button>,
            ]
      }
    >
      {isGenerating ? (
        <LoadingContainer>
          <LoadingIconWrapper>
            <SparkleLoader isAnimating={true} size={240} />
          </LoadingIconWrapper>
          <LoadingMessage>{loadingMessage}</LoadingMessage>
        </LoadingContainer>
      ) : (
        <>
          <ModalContentWrapper>
            <ModalSecondaryText>
              Copy the prompt below and paste it into any AI prototyping tool to generate your UI.
            </ModalSecondaryText>
          </ModalContentWrapper>
          <PromptTextArea value={prototypeText} readOnly rows={20} />
        </>
      )}
    </Modal>
  )
}
